'use client';

import React, { FC } from 'react';
import classNames from 'classnames';
import { useCartUrl } from '../hooks/useCartUrl';
import useFeature from '@/hooks/useFeature';
import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  PAGE_LOCATIONS,
  trackAsyncItemClickedEvent,
} from '@/utils/rudderstack';
import { Link } from '@/navigation';
import useCartContext from '@/context/CartContext';
import { CartSvg } from './CartSvg';

export interface CartProps {
  className?: string;
}

const CART_DISPLAY_COUNT_MAX = 99;

export const Cart: FC<CartProps> = ({ className }) => {
  const cartVariation = useFeature(
    'money_enable-cart-next-js-migration',
    'off',
  );
  const cartHref = useCartUrl({ cartVariation });
  const { cart } = useCartContext();
  const cartItemCount = cart?.totalQuantity || 0;

  const cartHasItems = cartItemCount > 0;
  const doesItemCountExceedCutoff = cartItemCount > CART_DISPLAY_COUNT_MAX;

  const displayCount = doesItemCountExceedCutoff
    ? `${CART_DISPLAY_COUNT_MAX}+`
    : String(cartItemCount);

  return (
    <Link
      aria-label="Cart"
      className={classNames('relative inline-block', className)}
      href={cartHref}
      onClick={async (e) => {
        e.preventDefault();

        await trackAsyncItemClickedEvent({
          url: window.location.href,
          type: CLICKED_ITEM_TYPES.IMAGE,
          pageLocation: PAGE_LOCATIONS.NAVIGATION,
          cta: CTA_NAMES.CART_ICON,
        });

        window.location.href = cartHref;
      }}
    >
      <CartSvg />

      {cartHasItems && (
        <span
          className={classNames(
            'absolute top-0 right-0 translate-x-[35%] -translate-y-[35%]',
            'flex items-center justify-center',
            'size-4 rounded-full bg-purple text-white',
            'text-[11px] leading-[20px] font-normal text-center whitespace-nowrap',
            { 'size-5': doesItemCountExceedCutoff },
          )}
          data-testid="cart-badge"
          aria-hidden="true"
        >
          {displayCount}
        </span>
      )}
    </Link>
  );
};

export default React.memo(Cart);
